import { OSDSlideViewer } from "../component/viewer";
import { useState, useEffect, useRef, useMemo } from "react";
import { UserCustomSetting } from "../api/user";
import styled from "styled-components";
import { InfoCircleOutlined, CommentOutlined, FileImageOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import DemoSideMenu from "../component/viewer/DemoSideMenu";
import { message, Tag, Tooltip } from "antd";
import { ViewerContext } from "./Viewer";
import { useRecoilState } from "recoil";
import { currentPostAtom, currentSlideAtom } from "../atom";
import { Slide, getSlideData, postAnnotation } from "../api/slideAPI";
import { MediaGroup, getMediaGroups } from "../api/mediaGroupAPI";
import {
  GroupFilter,
  UNGROUPED_FILTER,
  countByGroup,
  matchesGroupFilter,
} from "../component/post/mediaGroup";
import { ensureTempViewerIdentity } from "../lib/tempViewer";
import { getPostData } from "../api/postAPI";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getSlideMpp } from "../api/deepzoomAPI";
import InvalidLinkScreen from "../component/common/InvalidLinkScreen";

const DemoViewer = () => {
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [isSlideListOpen, setIsSlideListOpen] = useState<boolean>(true);
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [activeGroupFilter, setActiveGroupFilter] = useState<GroupFilter>(null);
  // 미디어 그룹(/api/media-groups) — 데모 뷰어는 읽기 전용 필터로만 쓴다
  const [mediaGroups, setMediaGroups] = useState<MediaGroup[]>([]);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isConsultationMode = searchParams.get('mode') === 'consultation';
  const slideListRef = useRef<HTMLDivElement>(null);
  const slideCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [userCustomSet, setUserCustomSet] = useState<UserCustomSetting>({
    useScaleBar: true,
    useMagnificationBar: true,
    useCaptureButton: true,
    useViewerRotationSlider: true,
    useGammaSlider: true,
    useViewerThumbnail: true,
    useAnnotation: true,
    useICCButton: true,
    userGammaValue: 1,
    useInvertedZoom: false,
  });



  const filteredSlideEntries = useMemo(
    () =>
      slides
        .map((s, i) => ({ slide: s, originalIndex: i }))
        .filter(({ slide }) => matchesGroupFilter(slide, activeGroupFilter)),
    [slides, activeGroupFilter]
  );

  // 그룹별 개수는 목록 API 가 주지 않으므로 슬라이드 목록에서 센다
  const groupCounts = useMemo(() => countByGroup(slides), [slides]);
  const hasGroups = mediaGroups.length > 0;

  const handleGroupFilterChange = (group: GroupFilter) => {
    setActiveGroupFilter(group);
    if (!matchesGroupFilter(slides[currentIndex], group)) {
      const firstIndex = slides.findIndex((s) => matchesGroupFilter(s, group));
      if (firstIndex !== -1) handleSlideClick(slides[firstIndex], firstIndex);
    }
  };

  const togglePanel = (panelName: string) => {
    setActivePanel(activePanel === panelName ? null : panelName);
  };

  useEffect(() => {
    const loadPost = async () => {
      if (id) {
        const data = await getPostData(+id);
        if (!data || !data.id) {
          setIsExpired(true);
          return;
        }
        setCurrentPost(data);

        if (data?.slides && data.slides.length > 0) {
          setSlides(data.slides);
          // 임시 링크 세션에서 그룹 조회가 막혀도 빈 배열이 와서 필터 바만 숨겨진다
          setMediaGroups(await getMediaGroups(+id, "SLIDE"));
          const firstSlide = data.slides[0];
          setCurrentSlide(firstSlide);
          setCurrentIndex(0);
          const initParams = new URLSearchParams();
          const mode = searchParams.get('mode');
          if (mode) initParams.set('mode', mode);
          initParams.set('slideId', firstSlide.slideId.toString());
          initParams.set('slideIndex', '1');
          navigate(
            `/demo/viewer/${id}?${initParams.toString()}`,
            { replace: true }
          );
        }
      }
    };
    loadPost();
  }, [id]);

  const handleSlideClick = (slideItem: Slide, index: number) => {
    setCurrentSlide(slideItem);
    setCurrentIndex(index);

    const params = new URLSearchParams(searchParams);
    params.set('slideId', slideItem.slideId.toString());
    params.set('slideName', slideItem.slidename || '');
    params.set('slideIndex', (index + 1).toString());

    navigate(`/demo/viewer/${id}?${params.toString()}`, { replace: true });
  };

  const handleSlideClickWithScroll = (slideItem: Slide, index: number) => {
    handleSlideClick(slideItem, index);

    const slideElement = slideCardRefs.current[index];
    if (slideElement) {
      slideElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  const handleGetMpp = async (postId: number, filename: string) => {
    return await getSlideMpp(postId, filename);
  };

  const handleGetAnnotations = async (slideId: number) => {
    const data = await getSlideData(slideId);
    setCurrentSlide((prev) => ({ ...prev, ...data }));
    return { annotations: data.annotations, slideComments: data.slideComments };
  };

  const handleSaveAnnotation = async (slideId: number, path: any, name: string) => {
    if (!isConsultationMode) {
      message.warning("어노테이션 생성 및 수정 권한이 없습니다");
      return;
    }
    const identity = ensureTempViewerIdentity();
    if (!identity) return;
    await postAnnotation(slideId, path, name, identity);
  };

  useEffect(() => {
    if (currentSlide && currentSlide.slideId) {
      setSlides(prevSlides =>
        prevSlides.map(slide =>
          slide.slideId === currentSlide.slideId ? currentSlide : slide
        )
      );
    }
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (filteredSlideEntries.length === 0) return;

      const listElement = slideListRef.current;
      if (!listElement) return;

      const posInFiltered = filteredSlideEntries.findIndex(e => e.originalIndex === currentIndex);

      if (e.key === 'ArrowLeft') {
        const prevPos = posInFiltered <= 0 ? filteredSlideEntries.length - 1 : posInFiltered - 1;
        const { slide, originalIndex } = filteredSlideEntries[prevPos];
        handleSlideClick(slide, originalIndex);

        if (posInFiltered === 0) {
          const lastSlide = slideCardRefs.current[originalIndex];
          if (lastSlide) {
            listElement.scrollTo({ left: lastSlide.offsetLeft, behavior: 'smooth' });
          }
        } else {
          setTimeout(() => {
            const slideElement = slideCardRefs.current[originalIndex];
            if (slideElement) {
              const listRect = listElement.getBoundingClientRect();
              const slideRect = slideElement.getBoundingClientRect();
              if (slideRect.left < listRect.left) {
                listElement.scrollTo({ left: slideElement.offsetLeft - 20, behavior: 'smooth' });
              }
            }
          }, 0);
        }
      } else if (e.key === 'ArrowRight') {
        const nextPos = posInFiltered >= filteredSlideEntries.length - 1 ? 0 : posInFiltered + 1;
        const { slide, originalIndex } = filteredSlideEntries[nextPos];
        handleSlideClick(slide, originalIndex);

        if (posInFiltered === filteredSlideEntries.length - 1) {
          listElement.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          setTimeout(() => {
            const slideElement = slideCardRefs.current[originalIndex];
            if (slideElement) {
              const listRect = listElement.getBoundingClientRect();
              const slideRect = slideElement.getBoundingClientRect();
              if (slideRect.right > listRect.right) {
                listElement.scrollTo({
                  left: slideElement.offsetLeft - listElement.clientWidth + slideElement.clientWidth + 20,
                  behavior: 'smooth'
                });
              }
            }
          }, 0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredSlideEntries, currentIndex, id]);

  useEffect(() => {
    slideCardRefs.current = slideCardRefs.current.slice(0, slides.length);
  }, [slides.length]);

  if (isExpired) {
    return <InvalidLinkScreen />;
  }

  return (
    <ViewerContext.Provider value={{
      userCustomSet,
      setUserCustomSet,
      activePanel,
      setActivePanel,
      togglePanel
    }}>
      <ViewerContainer>
        <ViewerArea>
          {currentSlide?.filename && currentPost?.id ? (
            <OSDSlideViewer
              filename={currentSlide.filename}
              postId={currentPost.id}
              slideId={currentSlide.slideId}
              isAnno={true}
              isDemo={!isConsultationMode}
              requirePasswordOnEdit={isConsultationMode}
              onGetSlideMpp={handleGetMpp}
              onGetAnnotations={handleGetAnnotations}
              onSaveAnnotation={handleSaveAnnotation}
              isReset={false}
              isUserSet={true}
              isSaveSet={false}
              isAnnotationButton={false}
              isCommentButton={false}
              useContextMenu={true}
            />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#fff' }}>
              No Slide Selected
            </div>
          )}
        </ViewerArea>

        <SlideListToggleButton
          onClick={() => setIsSlideListOpen(!isSlideListOpen)}
          $isOpen={isSlideListOpen}
          $hasGroups={hasGroups}
        >
          {isSlideListOpen ? <DownOutlined /> : <UpOutlined />}
        </SlideListToggleButton>

        <SlideListArea $isOpen={isSlideListOpen} $hasGroups={hasGroups}>
          <SlideListWrapper $isOpen={isSlideListOpen}>
            {hasGroups && (
              <GroupFilterBar>
                <Tag
                  color={activeGroupFilter == null ? "blue" : "default"}
                  onClick={() => handleGroupFilterChange(null)}
                  style={{ cursor: "pointer" }}
                >
                  All <SlideCountBadge $active={activeGroupFilter == null}>{groupCounts.total}</SlideCountBadge>
                </Tag>
                {groupCounts.ungrouped > 0 && (
                  <Tag
                    color={activeGroupFilter === UNGROUPED_FILTER ? "blue" : "default"}
                    onClick={() => handleGroupFilterChange(activeGroupFilter === UNGROUPED_FILTER ? null : UNGROUPED_FILTER)}
                    style={{ cursor: "pointer" }}
                  >
                    그룹없음 <SlideCountBadge $active={activeGroupFilter === UNGROUPED_FILTER}>{groupCounts.ungrouped}</SlideCountBadge>
                  </Tag>
                )}
                {mediaGroups.map((group) => {
                  const gid = group.mediaGroupId;
                  const isActive = activeGroupFilter === gid;
                  return (
                    <Tag
                      key={gid}
                      color={isActive ? "blue" : "default"}
                      onClick={() => handleGroupFilterChange(isActive ? null : gid)}
                      style={{ cursor: "pointer" }}
                    >
                      {group.name} <SlideCountBadge $active={isActive}>{groupCounts.byGroup[gid] ?? 0}</SlideCountBadge>
                    </Tag>
                  );
                })}
              </GroupFilterBar>
            )}
            <SlideListContent ref={slideListRef} $hasGroups={hasGroups}>
              {filteredSlideEntries.map(({ slide: slideItem, originalIndex: index }) => (
                <SlideCard
                  key={slideItem.slideId}
                  ref={(el) => (slideCardRefs.current[index] = el)}
                  $isActive={slideItem.slideId === currentSlide?.slideId}
                  onClick={() => handleSlideClickWithScroll(slideItem, index)}
                >
                  <SlideCardInner $isActive={slideItem.slideId === currentSlide?.slideId}>
                    <SlideHeader>
                      <SlideIndex>{index + 1}</SlideIndex>
                      <SlideFormat>{slideItem.filename.split('.').pop()?.toUpperCase() || ''}</SlideFormat>
                    </SlideHeader>
                    <SlideThumbnailWrapper>
                      <SlideThumbnail
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/photo/${slideItem.thumbnail}`}
                        alt={slideItem.slidename}
                      />
                    </SlideThumbnailWrapper>
                    <SlideFooter>
                      <Tooltip title={slideItem.slidename} placement="top">
                        <SlideName>{slideItem.slidename}</SlideName>
                      </Tooltip>
                    </SlideFooter>
                  </SlideCardInner>
                </SlideCard>
              ))}
            </SlideListContent>
          </SlideListWrapper>
        </SlideListArea>

        <SidePanel $isOpen={activePanel !== null}>
          <DemoSideMenu
            activePanel={activePanel}
            onClose={() => setActivePanel(null)}
            isConsultationMode={isConsultationMode}
            onRefreshAnnotations={async () => { if (currentSlide?.slideId) await handleGetAnnotations(currentSlide.slideId); }}
          />
        </SidePanel>

        <MiniBar>
          <MiniBarButton
            $active={activePanel === 'info'}
            onClick={() => togglePanel('info')}
            title="Information"
          >
            <InfoCircleOutlined />
          </MiniBarButton>
          <MiniBarButton
            $active={activePanel === 'annotation'}
            onClick={() => togglePanel('annotation')}
            title="Annotations"
          >
            <FileImageOutlined />
          </MiniBarButton>
          <MiniBarButton
            $active={activePanel === 'comment'}
            onClick={() => togglePanel('comment')}
            title="Comments"
          >
            <CommentOutlined />
          </MiniBarButton>
        </MiniBar>
      </ViewerContainer>
    </ViewerContext.Provider>
  );
};

const ViewerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-template-rows: 1fr auto;
  height: calc(100vh - 30px);
  overflow: hidden;
  position: relative;
`;

const ViewerArea = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const SlideListArea = styled.div<{ $isOpen: boolean; $hasGroups?: boolean }>`
  grid-column: 1 / 4;
  grid-row: 2;
  border-top: 1px solid #1e3a52;
  background-color: #0a1929;
  overflow: hidden;
  height: ${({ $isOpen, $hasGroups }) => $isOpen ? ($hasGroups ? '210px' : '180px') : '0px'};
  transition: height 0.3s ease-out;
  position: relative;

  @media (max-width: 1920px) {
    height: ${({ $isOpen, $hasGroups }) => $isOpen ? ($hasGroups ? '148px' : '120px') : '0px'};
  }

  @media (min-width: 2560px) {
    height: ${({ $isOpen, $hasGroups }) => $isOpen ? ($hasGroups ? '215px' : '185px') : '0px'};
  }
`;

const SlideListToggleButton = styled.button<{ $isOpen: boolean; $hasGroups?: boolean }>`
  position: fixed;
  bottom: ${({ $isOpen, $hasGroups }) => $isOpen ? ($hasGroups ? '210px' : '180px') : '0px'};
  right: 6px;

  @media (max-width: 1920px) {
    bottom: ${({ $isOpen, $hasGroups }) => $isOpen ? ($hasGroups ? '148px' : '120px') : '0px'};
  }

  @media (min-width: 2560px) {
    bottom: ${({ $isOpen, $hasGroups }) => $isOpen ? ($hasGroups ? '215px' : '185px') : '0px'};
  }

  background-color: #8899aa;
  border: 1px solid #1e3a52;
  border-bottom: ${({ $isOpen }) => ($isOpen ? 'none' : '1px solid #1e3a52')};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  color: #051e34;
  padding: 6px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 1000;
  transition: all 0.3s ease-out;

  svg {
    font-size: 12px;
  }

  span {
    color: #1e3a52;
  }

  &:hover {
    background-color: #e8f0f7;

    span {
      color: #051e34;
    }
  }
`;

const SlideListWrapper = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.2s ease-out;
`;

const GroupFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 16px 6px;
  align-items: center;
`;

const SlideCountBadge = styled.span<{ $active: boolean }>`
  display: inline-block;
  background: ${({ $active }) => ($active ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.08)")};
  border-radius: 8px;
  padding: 0 5px;
  font-size: 11px;
  margin-left: 3px;
  line-height: 1.4;
`;

const SlideListContent = styled.div<{ $hasGroups?: boolean }>`
  flex: 1;
  padding: ${({ $hasGroups }) => $hasGroups ? '6px 1rem 0' : '0 1rem'};
  display: flex;
  align-items: ${({ $hasGroups }) => $hasGroups ? 'flex-start' : 'center'};
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;

  @media (max-width: 1920px) {
    gap: 10px;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #051e34;
  }

  &::-webkit-scrollbar-thumb {
    background: #1e3a52;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #2d5a7b;
  }
`;

const SlideCard = styled.div<{ $isActive: boolean }>`
  flex-shrink: 0;
  width: 8rem;
  cursor: pointer;
  position: relative;

  @media (max-width: 1920px) {
    width: 6.5rem;
  }

  @media (min-width: 2560px) {
    width: 8.5rem;
  }
`;

const SlideCardInner = styled.div<{ $isActive: boolean }>`
  background-color: #ffffff;
  border: 2px solid ${props => props.$isActive ? '#1890ff' : '#e0e0e0'};
  overflow: hidden;
  transition: border-color 0.2s;
  height: 9.375rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 1920px) {
    height: 6.5rem;
  }

  @media (min-width: 2560px) {
    height: 9.875rem;
  }

  &:hover {
    border-color: #1890ff;
  }
`;

const SlideHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;

  @media (max-width: 1920px) {
    padding: 3px 6px;
  }
`;

const SlideIndex = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: #333;
`;

const SlideFormat = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  color: #666;
  background-color: #e8e8e8;
  padding: 2px 6px;
  border-radius: 3px;

  @media (max-width: 1920px) {
    font-size: 0.6rem;
    padding: 1px 4px;
  }
`;

const SlideThumbnailWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  padding: 8px;
  min-height: 0;

  @media (max-width: 1920px) {
    padding: 4px;
  }
`;

const SlideThumbnail = styled.img`
  max-height: 100%;
  object-fit: contain;
`;

const SlideFooter = styled.div`
  padding: 6px 8px;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
  min-height: 28px;

  @media (max-width: 1920px) {
    padding: 3px 6px;
    min-height: 20px;
  }
`;

const SlideName = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`;

const SidePanel = styled.div<{ $isOpen: boolean }>`
  grid-column: 2;
  grid-row: 1;
  width: ${({ $isOpen }) => ($isOpen ? '450px' : '0')};
  background-color: #0a1929;
  border-left: ${({ $isOpen }) => ($isOpen ? '1px solid #1e3a52' : 'none')};
  overflow: hidden;
  transition: width 0.3s ease-out;
  flex-shrink: 0;
`;

const MiniBar = styled.div`
  grid-column: 3;
  grid-row: 1;
  width: 60px;
  background-color: #051e34;
  border-left: 1px solid #1e3a52;
  display: flex;
  flex-direction: column;
  padding: 15px 0;
  gap: 8px;
  flex-shrink: 0;
`;

const MiniBarButton = styled.button<{ $active?: boolean }>`
  background-color: ${({ $active }) => ($active ? '#1890ff' : 'transparent')};
  border: none;
  color: ${({ $active }) => ($active ? '#fff' : '#8899aa')};
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }

  &:hover {
    background-color: ${({ $active }) => ($active ? '#1890ff' : '#1e3a52')};
    color: #fff;
  }
`;


export default DemoViewer;
